import { isUnknownArray } from "../types/data";
export const GOOGLE_ANALYTICS_MEASUREMENT_ID = "G-BYGYBNLF76";
export const SITE_OWNER_UID = "SsnjLmgBFnV9lZyQNGNKxLhYgi32";
export const OWNER_ANALYTICS_EXCLUSION_KEY = "cineparagon_ga4_owner_excluded_v1";

declare global {
  interface Window {
    [flag: `ga-disable-${string}`]: boolean | undefined;
    dataLayer?: IArguments[];
    gtag?: (...arguments_: unknown[]) => void;
    __cineparagonGa4Configured?: boolean;
    CineParagonAnalytics?: {
      clearOwnerExclusion: () => boolean;
      isOwnerExcluded: () => boolean;
    };
  }
}

const SCRIPT_ELEMENT_ID = "cineparagon-google-analytics";
const CONFIGURED_FLAG = "__cineparagonGa4Configured";
const MANUAL_API_NAME = "CineParagonAnalytics";
const guardedAuthInstances = new WeakSet();

const getBrowserWindow = () => (typeof window === "undefined" ? null : window);

const getDisableProperty = (): `ga-disable-${string}` => `ga-disable-${GOOGLE_ANALYTICS_MEASUREMENT_ID}`;

export const hasOwnerAnalyticsExclusion = () => {
  const browserWindow = getBrowserWindow();
  if (!browserWindow) return false;

  try {
    return browserWindow.localStorage?.getItem(OWNER_ANALYTICS_EXCLUSION_KEY) === "1";
  } catch {
    return false;
  }
};

const setAnalyticsDisabledForSession = (disabled: boolean) => {
  const browserWindow = getBrowserWindow();
  if (!browserWindow) return;

  try {
    browserWindow[getDisableProperty()] = Boolean(disabled);
  } catch {
    // Analytics must never affect application startup or navigation.
  }
};

export const disableOwnerAnalytics = () => {
  // Set the GA-supported runtime kill switch before touching storage. This
  // still blocks the current session when localStorage is unavailable.
  setAnalyticsDisabledForSession(true);

  const browserWindow = getBrowserWindow();
  if (!browserWindow) return false;

  try {
    browserWindow.localStorage?.setItem(OWNER_ANALYTICS_EXCLUSION_KEY, "1");
    return true;
  } catch {
    return false;
  }
};

export const clearOwnerAnalyticsExclusion = () => {
  const browserWindow = getBrowserWindow();
  if (!browserWindow) return false;

  try {
    browserWindow.localStorage?.removeItem(OWNER_ANALYTICS_EXCLUSION_KEY);
    // Keep GA disabled for the rest of the current page. After signing out,
    // reload the page to start a clean analytics test session. If the owner is
    // still signed in, the auth guard will correctly create the marker again.
    return true;
  } catch {
    return false;
  }
};

const analyticsIsDisabled = () => {
  const browserWindow = getBrowserWindow();
  if (!browserWindow) return true;
  return Boolean(browserWindow[getDisableProperty()]) || hasOwnerAnalyticsExclusion();
};

const ensureGtagQueue = () => {
  const browserWindow = getBrowserWindow();
  if (!browserWindow) return null;

  if (!isUnknownArray(browserWindow.dataLayer)) {
    browserWindow.dataLayer = [];
  }

  if (typeof browserWindow.gtag !== "function") {
    browserWindow.gtag = function cineparagonGtag() {
      if (analyticsIsDisabled()) return;
      browserWindow.dataLayer?.push(arguments);
    };
  }

  return browserWindow.gtag;
};

export const initializeGoogleAnalytics = () => {
  const browserWindow = getBrowserWindow();
  if (!browserWindow || typeof document === "undefined") return false;

  if (hasOwnerAnalyticsExclusion()) {
    setAnalyticsDisabledForSession(true);
    return false;
  }

  setAnalyticsDisabledForSession(false);
  const gtag = ensureGtagQueue();
  if (!gtag) return false;

  try {
    if (!document.getElementById(SCRIPT_ELEMENT_ID)) {
      const script = document.createElement("script");
      script.id = SCRIPT_ELEMENT_ID;
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
        GOOGLE_ANALYTICS_MEASUREMENT_ID
      )}`;
      // A failed analytics request is intentionally isolated from the app.
      script.onerror = () => {};
      document.head.appendChild(script);
    }

    if (!browserWindow[CONFIGURED_FLAG]) {
      gtag("js", new Date());
      gtag("config", GOOGLE_ANALYTICS_MEASUREMENT_ID);
      browserWindow[CONFIGURED_FLAG] = true;
    }
    return true;
  } catch {
    return false;
  }
};

export const syncGoogleAnalyticsWithUser = (userId?: string | null) => {
  if (String(userId || "") === SITE_OWNER_UID) {
    disableOwnerAnalytics();
    return "excluded";
  }

  if (hasOwnerAnalyticsExclusion()) {
    setAnalyticsDisabledForSession(true);
    return "excluded";
  }

  initializeGoogleAnalytics();
  return "enabled";
};

export const installGoogleAnalyticsManualControls = () => {
  const browserWindow = getBrowserWindow();
  if (!browserWindow) return;

  try {
    browserWindow[MANUAL_API_NAME] = {
      ...(browserWindow[MANUAL_API_NAME] || {}),
      clearOwnerExclusion: clearOwnerAnalyticsExclusion,
      isOwnerExcluded: hasOwnerAnalyticsExclusion
    };
  } catch {
    // Some hardened browsers may make window properties read-only.
  }
};

export interface AnalyticsAuth {
  onAuthStateChanged: (
    next: (user: { uid: string } | null) => void,
    error: (error: Error) => void
  ) => () => void;
}

export const installGoogleAnalyticsAuthGuard = (auth?: AnalyticsAuth | null) => {
  installGoogleAnalyticsManualControls();

  if (hasOwnerAnalyticsExclusion()) {
    setAnalyticsDisabledForSession(true);
  }

  if (!auth || typeof auth.onAuthStateChanged !== "function") {
    if (!hasOwnerAnalyticsExclusion()) initializeGoogleAnalytics();
    return () => {};
  }

  if (guardedAuthInstances.has(auth)) return () => {};
  guardedAuthInstances.add(auth);

  try {
    return auth.onAuthStateChanged(
      (user) => {
        try {
          syncGoogleAnalyticsWithUser(user?.uid);
        } catch {
          // Analytics failures must not leak into Firebase auth callbacks.
        }
      },
      () => {
        // If auth itself cannot be restored, retain a known owner exclusion;
        // otherwise keep analytics available to ordinary anonymous visitors.
        try {
          if (hasOwnerAnalyticsExclusion()) {
            setAnalyticsDisabledForSession(true);
          } else {
            initializeGoogleAnalytics();
          }
        } catch {
          // No-op by design.
        }
      }
    );
  } catch {
    if (!hasOwnerAnalyticsExclusion()) initializeGoogleAnalytics();
    return () => {};
  }
};
